import React, { useEffect, useMemo, useState } from "react";
import useResponsiveFlags from "../../hooks/useResponsiveFlags";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import axios from "../../api/axios";
import FilterMenuUsers from "./FilterMenuUsers";
import UserTableHead from "./UserTableHead";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Typography,
  Grid2,
  Alert,
  Badge,
} from "@mui/material";
import WrapperContainer from "../../components/utilities/WrapperContainer";

import ActivationStatusCell from "./ActivationStatusCell";
import AccessControlCell from "./AccessControlCell";

import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";

function badgeIconFunction(request) {
  if (request) {
    return "User has requested for mudule access";
  }
}

const UserTable = () => {
  const { isDesktop, isSmallMobileScreen } = useResponsiveFlags();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("regdate");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    user: null,
    action: "",
    accessType: null,
  });

  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    if (!search) handleSearchButtonClick();
  }, [search]);

  // useEffect(() => {
  //   if (errorMessage || successMessage) {
  //     const timer = setTimeout(() => {
  //       if (successMessage) {
  //         setSuccessMessage("");
  //       } else if (errorMessage) {
  //         setErrorMessage("");
  //       }
  //     }, 10000);

  //     return () => clearTimeout(timer);
  //   }
  // }, [errorMessage, successMessage]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    axios
      .get("/api/admin_dashboard/gettokenorgs")
      .then((response) => {
        setOrgs(response.data || []);
      })
      .catch((err) => {
        setErrorMessage(
          "Failed to fetch Organizations for token creation form.",
        );
      });
  };

  const handleAssignOrg = async () => {
    if (!selectedOrg) {
      setErrorMessage("Please select an organization to assign.");
      return;
    }
    if (!selectedRow) {
      setErrorMessage("Please select at least one user to assign.");
      return;
    }
    setErrorMessage("");
    setSuccessMessage("");
    axios
      .post("/api/admin_dashboard/changeuserorganization", {
        userId: selectedRow,
        orgId: selectedOrg,
      })
      .then((response) => {
        if (response.data.success) {
          setSuccessMessage(response.data.message);
          fetchData();
        } else {
          setErrorMessage(
            response.data.message || "Failed to change the user organisation",
          );
        }
        setSelectedOrg("");
        setSelectedRow("");
      })
      .catch((err) => {
        setErrorMessage("Failed to change the user organisation");
        setSelectedOrg("");
        setSelectedRow("");
      });
  };

  const BootstrapTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.common.black,
    },
  }));

  const fetchData = async () => {
    setDataLoading(true);
    setErrorMessage("");
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
    };
    if (order) params.sort_order = order;
    if (orderBy) params.sort_by = orderBy;
    if (filters.startDate) params.date_from = filters.startDate;
    if (filters.endDate) params.date_to = filters.endDate;
    if (search) params.search = search;
    axios
      .get("/api/admin_dashboard/usersdata", { params })
      .then((response) => {
        const result = response.data;
        setData(result.data || []);
        setTotalCount(result.total || 0);
        setDataLoading(false);
      })
      .catch((err) => {
        setErrorMessage("Failed to fetch users table data.");
        setDataLoading(false);
      });
  };

  const handleSelectRow = (userId) => {
    setSelectedRow((prev) => (prev === userId ? null : userId));
  };
  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [column]: prevFilters[column] === value ? null : value,
    }));
    setPage(0);
  };
  const handleSearchChange = (event) => {
    setSearch(event.target.value.toLowerCase());
  };
  const handleSearchButtonClick = () => {
    if (page != 0) setPage(0);
    else fetchData();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    return [...data].sort(getComparator(order, orderBy));
  }, [data, order, orderBy]);

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    if (orderBy === "regdate" || orderBy === "lastused") {
      valueA = valueA ? new Date(valueA) : null;
      valueB = valueB ? new Date(valueB) : null;
    }

    if (orderBy === "id" || orderBy === "runs_count") {
      valueA = Number(valueA);
      valueB = Number(valueB);
    }

    if (typeof valueA === "string") valueA = valueA.toLowerCase();
    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA == null) return 1;
    if (valueB == null) return -1;

    if (valueB < valueA) return -1;
    if (valueB > valueA) return 1;
    return 0;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayName = days[date.getDay()];
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    return `${dayName} ${month}/${day}/${year}`;
  };

  const performStatusAction = async () => {
    const { user, action } = confirmDialog;
    if (!user || !action) return;
    console.log(`Confirmed action "${action}" on user:`, user.id);

    let url = "/api/admin_dashboard/";
    switch (action) {
      case "approve":
      case "deny":
        url += `handleuseraccessaction`;
        break;
      case "resend_verification":
        url += `resend-verification`;
        break;
      default:
        return;
    }
    axios
      .post(url, { userId: user.id, action: action })
      .then((response) => {
        if (response.data.success) {
          setSuccessMessage(response.data.message);
          fetchData();
        } else {
          setErrorMessage(
            response.data.message ||
              `Failed to perform ${action} on user ${user.fname} ${user.lname} due to unknown error.`,
          );
        }
        setConfirmDialog({
          open: false,
          user: null,
          action: "",
          accessType: null,
        });
      })
      .catch((err) => {
        setErrorMessage(
          `Failed to perform ${action} on user ${user.fname} ${user.lname} due to unknown error.`,
        );
        setConfirmDialog({
          open: false,
          user: null,
          action: "",
          accessType: null,
        });
      });
  };

  const performAccessAction = async () => {
    const { user, action, accessType } = confirmDialog;
    if (!user || !action || !accessType) return;

    axios
      .post("/api/admin_dashboard/accessmodule", {
        user: user.id,
        accessType: accessType,
        action: action,
      })
      .then((response) => {
        if (response.data.success) {
          const accessLabel = accessType === "fraction" ? "Fraction" : "HiRes";
          setSuccessMessage(
            `${accessLabel} access ${action === "grant" ? "granted" : "denied"} successfully.`,
          );
          fetchData();
        } else {
          setErrorMessage(
            response.data.message ||
              "Failed to update the module access status.",
          );
        }
        setConfirmDialog({
          open: false,
          user: null,
          action: "",
          accessType: null,
        });
      })
      .catch((err) => {
        setErrorMessage("Failed to update the module access status.");
        setConfirmDialog({
          open: false,
          user: null,
          action: "",
          accessType: null,
        });
      });
  };

  const handleAccessAction = (user, accessType, action) => {
    setConfirmDialog({
      open: true,
      user,
      action,
      accessType,
    });
  };

  // Clear messages after timeout

  const getConfirmDialogContent = () => {
    const { user, action, accessType } = confirmDialog;

    if (accessType) {
      const accessLabel = accessType === "fraction" ? "Fraction" : "Hi-Res";
      const actionLabel = action === "grant" ? "grant" : "deny";
      return {
        title: `Confirm ${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} ${accessLabel} Access`,
        message: `Are you sure you want to ${actionLabel} ${accessLabel} access for ${user?.email}?`,
        confirmLabel: `Yes, ${actionLabel}`,
        onConfirm: performAccessAction,
      };
    } else {
      return {
        title: "Confirm Action",
        message: `Are you sure you want to ${action} user (${user?.email})?`,
        confirmLabel: `Yes, ${action}`,
        onConfirm: performStatusAction,
      };
    }
  };

  const dialogContent = getConfirmDialogContent();

  return (
    <>
      <WrapperContainer
        title="Users"
        loggedin={true}
        content={
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <h6 style={{ width: "100%" }}>
                  Please select the organization to assign to selected users:{" "}
                  {selectedRow && (
                    <span
                      style={{
                        color: "#0061bb",
                      }}
                    >
                      User with Id {selectedRow} has been selected.
                    </span>
                  )}
                </h6>
                <Grid2 container spacing={2} sx={{ mt: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Organization *</InputLabel>
                      <Select
                        required
                        value={selectedOrg}
                        onChange={(e) => setSelectedOrg(e.target.value)}
                        label="Organization"
                      >
                        {orgs.map((org) => (
                          <MenuItem key={org.id} value={org.id}>
                            {org.id} - {org.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 4, md: 3, lg: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        width: {
                          xs: "100%",
                          sm: "auto",
                        },
                      }}
                      onClick={handleAssignOrg}
                    >
                      Submit
                    </Button>
                  </Grid2>
                </Grid2>
              </CardContent>
            </Card>
            <Dialog
              open={confirmDialog.open}
              onClose={() =>
                setConfirmDialog({
                  open: false,
                  user: null,
                  action: "",
                  accessType: null,
                })
              }
            >
              <DialogTitle>{dialogContent.title}</DialogTitle>
              <DialogContent>{dialogContent.message}</DialogContent>
              <DialogActions>
                <Button
                  onClick={() =>
                    setConfirmDialog({
                      open: false,
                      user: null,
                      action: "",
                      accessType: null,
                    })
                  }
                >
                  Cancel
                </Button>
                <Button
                  onClick={dialogContent.onConfirm}
                  color="error"
                  variant="contained"
                >
                  {dialogContent.confirmLabel}
                </Button>
              </DialogActions>
            </Dialog>

            <div className="d-flex flex-column-reverse flex-xl-row justify-content-between mb-xl-3">
              <div className="d-flex align-items-center">
                <input
                  type="text"
                  className="form-control search-icon"
                  placeholder="Search"
                  value={search}
                  onChange={handleSearchChange}
                />
                <Button
                  size="medium"
                  variant="contained"
                  onClick={handleSearchButtonClick}
                  sx={{ padding: "6px 0", marginLeft: "10px" }}
                >
                  Search
                </Button>
                <FilterMenuUsers
                  handleFilterChange={handleFilterChange}
                  filters={filters}
                />
              </div>
            </div>

            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage("")}
                sx={{ mt: 1 }}
              >
                {errorMessage}
              </Alert>
            )}

            {successMessage && (
              <Alert
                severity="success"
                onClose={() => setSuccessMessage("")}
                sx={{ mt: 1 }}
              >
                {successMessage}
              </Alert>
            )}

            <TableContainer
              className="table-scroll-visible"
              sx={{
                height: isDesktop
                  ? "calc(100vh - 370px)"
                  : "calc(100vh - 440px)",
                minHeight: isDesktop ? "500px" : "300px",
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              <Table
                stickyHeader
                sx={{ minWidth: 750 }}
                aria-label="user table"
              >
                <UserTableHead
                  order={order}
                  orderBy={orderBy}
                  onSort={handleSort}
                />
                <TableBody>
                  {dataLoading ? (
                    <TableRow>
                      <TableCell colSpan={15} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : sortedData.length > 0 ? (
                    sortedData.map((row) => {
                      const isNewPendingUser =
                        row.is_activated === 0 &&
                        row.created_at &&
                        (new Date() - new Date(row.created_at)) /
                          (1000 * 60 * 60 * 24) <
                          7;

                      return (
                        <TableRow
                          key={row.id}
                          style={{
                            backgroundColor: isNewPendingUser
                              ? "#aaacb5"
                              : "#fff",
                          }}
                        >
                          <TableCell
                            style={
                              !isSmallMobileScreen
                                ? {
                                    position: "sticky",
                                    top: 0,
                                    left: 0,
                                    zIndex: 10,
                                    background: isNewPendingUser
                                      ? "#aaacb5"
                                      : "#fff",
                                  }
                                : {}
                            }
                          >
                            <input
                              type="checkbox"
                              checked={selectedRow === row.id}
                              onChange={() => handleSelectRow(row.id)}
                            />
                          </TableCell>
                          <TableCell
                            style={
                              !isSmallMobileScreen
                                ? {
                                    position: "sticky",
                                    top: 0,
                                    left: 45,
                                    zIndex: 10,
                                    background: isNewPendingUser
                                      ? "#aaacb5"
                                      : "#fff",
                                  }
                                : {}
                            }
                            className="custom-size"
                          >
                            {row.id}
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.lname || "N/A"}
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.fname || "N/A"}
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.org || "N/A"}
                          </TableCell>
                          <TableCell className="custom-size">
                            <ActivationStatusCell
                              user={row}
                              onAction={(user, action) =>
                                setConfirmDialog({
                                  open: true,
                                  user,
                                  action,
                                  accessType: null,
                                })
                              }
                            />
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.email}
                          </TableCell>
                          <TableCell className="custom-size">
                            {formatDate(row.regdate)}
                          </TableCell>
                          <TableCell className="custom-size">
                            {formatDate(row.lastused)}
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.runs || 0}
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.city || "N/A"}
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.state || "N/A"}
                          </TableCell>
                          <TableCell className="custom-size">
                            {row.country || "N/A"}
                          </TableCell>
                          <TableCell className="custom-size">
                            <Tooltip
                              title={badgeIconFunction(
                                row.frac_requested && row.frac_access === 0,
                              )}
                              placement="top"
                            >
                              <Badge
                                anchorOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                variant="dot"
                                invisible={
                                  !(row.frac_requested && row.frac_access === 0)
                                }
                                color="error"
                              >
                                <AccessControlCell
                                  user={row}
                                  accessType="fraction"
                                  onAction={handleAccessAction}
                                />
                              </Badge>
                            </Tooltip>
                          </TableCell>
                          <TableCell className="custom-size">
                            <Tooltip
                              title={badgeIconFunction(
                                row.hires_requested && row.hires_access === 0,
                              )}
                              placement="top"
                            >
                              <Badge
                                anchorOrigin={{
                                  vertical: "top",
                                  horizontal: "left",
                                }}
                                variant="dot"
                                invisible={
                                  !(
                                    row.hires_requested &&
                                    row.hires_access === 0
                                  )
                                }
                                color="error"
                              >
                                <AccessControlCell
                                  user={row}
                                  accessType="hires"
                                  onAction={handleAccessAction}
                                />
                              </Badge>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={15} align="center">
                        {search ? "No matching users found." : "No users found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {!dataLoading && totalCount > 0 && (
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count} total users`
                }
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    alignItems: "center",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    margin: 0,
                  },
                  "& .MuiTablePagination-displayedRows": {
                    margin: 0,
                  },
                }}
              />
            )}
          </>
        }
      />
    </>
  );
};

export default UserTable;
